import create from "zustand";
import _ from "lodash";
import { AppControlData, SignatureData, SignaturesResponse } from "@/api/signatures";

export interface SignaturesMap {
	[id: string]: SignatureData;
}

export interface NativeAppControlsMap {
	[id: string]: AppControlData;
}

/** Maps a lower-cased signature url to its signature id. */
export interface SignatureIdsByUrlMap {
	[url: string]: string;
}

interface State {
	signatures: SignatureData[];
	appControls: AppControlData[];
	signaturesMap: SignaturesMap;
	signaturesByUrlMap: SignatureIdsByUrlMap;
	nativeAppControlsMap: NativeAppControlsMap;
}

interface Actions {
	/**
	 * Replaces the stored signatures (sorted by name) and app controls, rebuilding the lookup maps.
	 * Intended to be fed from the react-query result for the signatures endpoint.
	 */
	setSignatures: (data: SignaturesResponse) => void;
	/** Returns the signature's display name, falling back to the id when unknown. */
	lookupName: (id: string) => string;
	/** Returns the best-available favicon url for the given signature id, or an empty string when none is available. */
	getFavicon: (id: string) => string;
	/** Finds a signature by id. */
	findSignature: (id: string) => SignatureData | undefined;
	/** Finds a signature by its url. */
	findSignatureByUrl: (url: string) => SignatureData | undefined;
	/** Finds a native app control by id. */
	findAppControl: (id: string) => AppControlData | undefined;
}

export const useSignatureStore = create<State & Actions>((set, get) => ({
	signatures: [],
	appControls: [],
	signaturesMap: {},
	signaturesByUrlMap: {},
	nativeAppControlsMap: {},

	setSignatures: (data) => {
		const signaturesByUrlMap: SignatureIdsByUrlMap = {};
		for (const signature of data.signatures) {
			const url = signature.url?.toLowerCase();
			// Skip empty urls, the "none" sentinel, and keep the first signature seen for a url.
			if (url && url !== "none" && signaturesByUrlMap[url] === undefined) {
				signaturesByUrlMap[url] = signature.id;
			}
		}

		set(() => ({
			// Sort a copy so pickers can iterate A–Z. The URL map above must keep API order (first-wins).
			signatures: [...data.signatures].sort((a, b) => a.name.localeCompare(b.name)),
			appControls: data.appControls,
			signaturesMap: _.keyBy(data.signatures, "id"),
			signaturesByUrlMap,
			nativeAppControlsMap: _.keyBy(data.appControls, "id"),
		}));
	},

	lookupName: (id) => {
		const signature = get().signaturesMap[id];
		return signature ? signature.name : id;
	},

	getFavicon: (id) => {
		const signature = get().signaturesMap[id];
		if (signature?.favicon_url) {
			return signature.favicon_url;
		}
		const url = signature?.url;
		if (url && url !== "none") {
			return `https://${url}/favicon.ico`;
		}
		return "";
	},

	findSignature: (id) => get().signaturesMap[id],

	findSignatureByUrl: (url) => {
		const id = get().signaturesByUrlMap[url.toLowerCase()];
		return id !== undefined ? get().signaturesMap[id] : undefined;
	},

	findAppControl: (id) => get().nativeAppControlsMap[id],
}));
